import type { PromptTemplateDTO } from "./dto.js";
import { MULTIPACK_DECISION_PROMPT } from "./multipack-prompts.js";
import { DEFAULT_WB_IMAGE_PLAN_PROMPT, DEFAULT_WB_IMAGE_GENERATION_PROMPT, DEFAULT_WB_COLOR_IMAGE_PROMPT, DEFAULT_WB_TITLE_PROMPT } from "./image-prompts.js";

export const PROMPT_FIELDS = ["imagePlanPrompt", "imageGenerationPrompt", "colorImagePrompt", "wildberriesTitlePrompt"] as const;
export type PromptTemplateSettings = { promptTemplates: PromptTemplateDTO[]; defaultPromptTemplateId: string } & Pick<PromptTemplateDTO, typeof PROMPT_FIELDS[number]>;
const limits = { imagePlanPrompt: 8000, imageGenerationPrompt: 8000, colorImagePrompt: 4000, wildberriesTitlePrompt: 4000 };
export function defaultPromptTemplate(): PromptTemplateDTO {
  return { id: "default", name: "默认模板", multipackEnabled: false, multipackDecisionPrompt: MULTIPACK_DECISION_PROMPT, imagePlanPrompt: DEFAULT_WB_IMAGE_PLAN_PROMPT, imageGenerationPrompt: DEFAULT_WB_IMAGE_GENERATION_PROMPT, colorImagePrompt: DEFAULT_WB_COLOR_IMAGE_PROMPT, wildberriesTitlePrompt: DEFAULT_WB_TITLE_PROMPT };
}
function validateTemplates(input: Record<string, unknown>): PromptTemplateSettings {
  if (!Array.isArray(input["promptTemplates"]) || !input["promptTemplates"].length) throw new Error("至少保留一个提示词模板");
  const ids = new Set<string>();
  const promptTemplates = input["promptTemplates"].map((item: unknown) => {
    if (!item || typeof item !== "object") throw new Error("提示词模板无效");
    const source = item as Record<string, unknown>;
    if (typeof source["id"] !== "string" || !source["id"].trim() || source["id"].length > 200 || ids.has(source["id"])) throw new Error("提示词模板 ID 无效或重复");
    ids.add(source["id"]);
    if (typeof source["name"] !== "string" || !source["name"].trim() || source["name"].length > 200) throw new Error("模板名称不能为空且最多 200 字符");
    const template = { id: source["id"], name: source["name"].trim() } as PromptTemplateDTO;
    for (const field of PROMPT_FIELDS) {
      const value = source[field];
      if (typeof value !== "string" || value.length > limits[field] || (field !== "colorImagePrompt" && !value.trim())) throw new Error("模板提示词不能为空或超过长度限制");
      template[field] = value;
    }
    const enabled = source['multipackEnabled'] ?? false;
    const decisionPrompt = source['multipackDecisionPrompt'] ?? MULTIPACK_DECISION_PROMPT;
    if (typeof enabled !== 'boolean' || typeof decisionPrompt !== 'string' || decisionPrompt.length > 8000 || (enabled && !decisionPrompt.trim())) throw new Error('多件装开关或判断提示词无效，启用时提示词不能为空');
    template.multipackEnabled = enabled;
    template.multipackDecisionPrompt = decisionPrompt;
    return template;
  });
  const active = promptTemplates.find(template => template.id === input["defaultPromptTemplateId"]);
  if (!active) throw new Error("请选择有效的默认启用模板");
  return { promptTemplates, defaultPromptTemplateId: active.id, ...Object.fromEntries(PROMPT_FIELDS.map(field => [field, active[field]])) } as PromptTemplateSettings;
}
export function resolvePromptTemplates(input: Record<string, unknown>): PromptTemplateSettings {
  if (input["promptTemplates"] !== undefined) return validateTemplates(input);
  const template = defaultPromptTemplate();
  if (PROMPT_FIELDS.some(field => typeof input[field] === "string")) { template.id = "legacy-default"; template.name = "原有模板"; }
  for (const field of PROMPT_FIELDS) {
    const value = input[field];
    if (typeof value === "string" && (field === "colorImagePrompt" || value.trim())) template[field] = value.slice(0, limits[field]);
  }
  return validateTemplates({ promptTemplates: [template], defaultPromptTemplateId: template.id });
}
export function mergePromptTemplates(input: Record<string, unknown>, existing: Record<string, unknown>): PromptTemplateSettings {
  const current = resolvePromptTemplates(existing);
  if (Array.isArray(input['promptTemplates'])) return validateTemplates({ ...input, promptTemplates: input['promptTemplates'].map(item => {
    if (!item || typeof item !== 'object') return item;
    const source = item as Record<string, unknown>;
    const previous = current.promptTemplates.find(template => template.id === source['id']);
    return { multipackEnabled: previous?.multipackEnabled, multipackDecisionPrompt: previous?.multipackDecisionPrompt, ...source };
  }) });
  if (input['promptTemplates'] !== undefined) return validateTemplates(input);
  const promptTemplates = current.promptTemplates.map(template => {
    if (template.id !== current.defaultPromptTemplateId) return template;
    const updated = { ...template };
    for (const field of PROMPT_FIELDS) if (typeof input[field] === "string") updated[field] = input[field];
    return updated;
  });
  return validateTemplates({ promptTemplates, defaultPromptTemplateId: current.defaultPromptTemplateId });
}
